import React from "react";
import { Box, Presentation, Video, BotMessageSquare, Users, Users2, CalendarPlus } from "lucide-react";
import { ReactComponent as Members } from "../assets/icons/sidebar/members.svg";
import { ReactComponent as ApiKeys } from "../assets/icons/sidebar/apiKeys.svg";

export const CARD_COLOR = "#7F56D9";
export const CARD_LIGHT_COLOR = "#F4EBFF";
export const CARD_BORDER_COLOR = "#D6BBFB";

export const EmbedIcon = ({ color = CARD_COLOR } = {}) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.66667 14.1667L2.5 10L6.66667 5.83334M13.3333 5.83334L17.5 10L13.3333 14.1667"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const DefaultIcon = ({ color = CARD_COLOR } = {}) => (
  <Box size={22} color={color} strokeWidth={2} />
);

export const ROUTE_MAP = {
  meetings: "/meetings",
  api: "/api-keys",
  website_translation: "/emotii-embed",
  realtime_api: "/realtime-api",
  presentations: "/presentations",
  video_tool: "/video-translation",
  emotii_for_teams: "/emotii-for-teams",
  video_translation_api: "/video-api",
  meetings_api: "/meetings-api",
  conferences: "/conferences"
};

export const PRODUCTS = {
  meetings: {
    title: "Meetings",
    description: "Real-time multilingual meetings where everyone speaks freely and listens in their chosen language with intent and nuance fully preserved.",
    icon: (color = CARD_COLOR) => <Members width="20" height="20" style={{ color }} />,
    features: ["Real-time interpretation", "Meeting recording and transcription", "AI Summarization in any language", "Speech style customization", "Audio & video support"],
  },
  api: {
    title: "API",
    description: "Embed emotion-aware, multilingual intelligence directly into your own product and systems.",
    icon: (color = CARD_COLOR) => <ApiKeys width="20" height="20" style={{ color }} />,
    features: ["Text-to-text translation and interpretation", "Easy to integrate across web, apps, and enterprise tools", "Multilingual, emotion-aware", "Ability to translate audio files"],
  },
  website_translation: {
    title: "Website Plugin",
    description: "Translate your website automatically, accurately, and at scale, preserving brand voice, emotion, and cultural relevance.",
    icon: (color = CARD_COLOR) => <EmbedIcon color={color} />,
    features: ["Emotionally aware AI translation", "Plug-and-play deployment", "All 126 languages included", "Secure & scalable"],
  },
  video_tool: {
    title: "Video AI Studio",
    description: "Translate your videos into multiple languages without losing meaning.",
    icon: (color = CARD_COLOR) => <Video width="20" height="20" style={{ color }} />,
    features: ["Preserves meaning and context", "Speech aligned with video timing", "Fast automated processing", "Easy upload and export"],
  },
  presentations: {
    title: "Presentation AI Studio",
    description: "Translate presentations within minutes while preserving layout and design integrity.",
    icon: (color = CARD_COLOR) => <Presentation width="20" height="20" style={{ color }} />,
    features: ["Smart text detection", "Context preservation", "Layout reconstruction", "Easy upload and export with editable output"],
  },
  realtime_api: {
    title: "API for Voice",
    description: "Embed emotion-aware, multilingual real-time interpretation and translation directly into your own product and systems.",
    icon: (color = CARD_COLOR) => <ApiKeys width="20" height="20" style={{ color }} />,
    features: ["Real-time translation and interpretation ", "Easy to integrate across web, apps, and enterprise tools", "Preserves tone and culture", "Configurable Text to Speech speed"],
  },
  emotii_for_teams: {
    title: "emotii for Teams",
    description: "Real-time AI interpretation and translation built natively into Microsoft Teams.",
    icon: (color = CARD_COLOR) => <Users width="20" height="20" style={{ color }} />,
    features: ["Runs directly inside Microsoft Teams", "Real-time emotion-aware interpretation", "Seat-based access per user", "Words and audio minutes per seat"],
  },
  video_translation_api: {
    title: "API for Video",
    description: "Embed emotion-aware, multilingual video translation and dubbing directly into your own product and systems.",
    icon: (color = CARD_COLOR) => <Video width="20" height="20" style={{ color }} />,
    features: ["Automated video translation and dubbing", "Easy to integrate across web, apps, and enterprise tools", "Preserves meaning and speech timing", "Optional subtitle burn-in"],
  },
  meetings_api: {
    title: "API for Meetings",
    description: "Create and manage multilingual emotii meetings directly from your own CRM, ERP or scheduling system.",
    icon: (color = CARD_COLOR) => <CalendarPlus width="20" height="20" style={{ color }} />,
    features: ["Create, update and cancel meetings via API", "Real-time emotion-aware interpretation", "AI summaries and transcripts", "Words, audio and video minutes per plan"],
  },
  conferences: {
    title: "Conferences",
    description: "Real-time multilingual interpretation for large-scale conferences, so every attendee can listen in their own language.",
    icon: (color = CARD_COLOR) => <Users2 width="20" height="20" style={{ color }} />,
    features: ["Real-time interpretation for every attendee", "Organizer-managed access", "Usage tracking per conference", "Scales to large audiences"],
  },
  chatbot: {
    title: "Chatbot",
    description: "Emotion-aware, conversational multilingual chatbots, responding naturally in the user’s language.",
    icon: (color = CARD_COLOR) => <BotMessageSquare width="20" height="20" style={{ color }} />,
    features: ["Real-time, emotion-aware responses ", "Deployable across web, apps, and enterprise tools, and enterprise tools", "Contextual understanding (not keyword-based)"],
  },
};


export const renderProductIcon = (slug, color) => {
  const iconFn = PRODUCTS[slug]?.icon;
  return iconFn ? iconFn(color) : <DefaultIcon color={color} />;
};

export const productSlugForPath = (path) =>
  Object.keys(ROUTE_MAP).find((slug) => ROUTE_MAP[slug] === path);
